import React, { useState, useMemo, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UserStatsCards from './components/UserStatsCards';
import UserFilters from './components/UserFilters';
import BulkActionsBar from './components/BulkActionsBar';
import UserTableRow from './components/UserTableRow';
import MobileUserCard from './components/MobileUserCard';
import CreateUserModal from './components/CreateUserModal';
import EditUserModal from './components/EditUserModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';

const UserManagement = () => {
  const { userProfile, isAuthenticated } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersThisMonth: 0,
    activeStudents: 0,
    studentEngagementRate: 0,
    totalAdmins: 0,
    adminCount: 0,
    inactiveUsers: 0,
    inactivePercentage: 0
  });

  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    activity: 'all',
    registration: 'all',
    search: ''
  });

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Load users and stats on mount
  useEffect(() => {
    loadUsers();
    loadStats();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await userService?.getAll(filters);
      setUsers(data);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await userService?.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // Reload users when filters change
  useEffect(() => {
    if (!loading) {
      loadUsers();
    }
  }, [filters?.role, filters?.status, filters?.search]);

  const filteredUsers = useMemo(() => {
    return users?.filter(user => {
      if (filters?.activity !== 'all') {
        const daysSinceLogin = Math.floor((new Date() - new Date(user?.lastLogin)) / (1000 * 60 * 60 * 24));
        if (filters?.activity === 'today' && daysSinceLogin > 0) return false;
        if (filters?.activity === 'week' && daysSinceLogin > 7) return false;
        if (filters?.activity === 'month' && daysSinceLogin > 30) return false;
        if (filters?.activity === 'inactive' && daysSinceLogin <= 30) return false;
      }

      if (filters?.registration !== 'all') {
        const daysSinceReg = Math.floor((new Date() - new Date(user?.registeredDate)) / (1000 * 60 * 60 * 24));
        if (filters?.registration === 'today' && daysSinceReg > 0) return false;
        if (filters?.registration === 'week' && daysSinceReg > 7) return false;
        if (filters?.registration === 'month' && daysSinceReg > 30) return false;
        if (filters?.registration === 'year' && daysSinceReg > 365) return false;
      }

      return true;
    });
  }, [users, filters]);

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];
    sorted?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'lastLogin' || sortConfig?.key === 'registeredDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredUsers, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      role: 'all',
      status: 'all',
      activity: 'all',
      registration: 'all',
      search: ''
    });
  };

  const handleSelectUser = (userId, checked) => {
    setSelectedUsers(prev => 
      checked ? [...prev, userId] : prev?.filter(id => id !== userId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedUsers(checked ? sortedUsers?.map(u => u?.id) : []);
  };

  const handleBulkAction = async (action) => {
    try {
      setError('');
      if (action === 'delete') {
        await userService?.bulkDelete(selectedUsers);
      } else if (action === 'activate' || action === 'deactivate') {
        const newStatus = action === 'activate' ? 'active' : 'inactive';
        await userService?.bulkUpdateStatus(selectedUsers, newStatus);
      }
      setSelectedUsers([]);
      await loadUsers();
      await loadStats();
    } catch (err) {
      setError(err?.message || `Failed to ${action} users`);
      console.error(`Error bulk ${action}:`, err);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      setError('');
      await userService?.create(userData);
      setIsCreateModalOpen(false);
      await loadUsers();
      await loadStats();
    } catch (err) {
      setError(err?.message || 'Failed to create user');
      console.error('Error creating user:', err);
    }
  };

  const handleEditUser = async (userData) => {
    try {
      setError('');
      await userService?.update(userData?.id, userData);
      setIsEditModalOpen(false);
      setSelectedUser(null);
      await loadUsers();
      await loadStats();
    } catch (err) {
      setError(err?.message || 'Failed to update user');
      console.error('Error updating user:', err);
    }
  };

  const handleDeleteUser = (userId) => {
    const user = users?.find(u => u?.id === userId);
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setError('');
      await userService?.delete(selectedUser?.id);
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      await loadUsers();
      await loadStats();
    } catch (err) {
      setError(err?.message || 'Failed to delete user');
      console.error('Error deleting user:', err);
    }
  };

  const handleToggleStatus = async (userId, newStatus) => {
    try {
      setError('');
      await userService?.toggleStatus(userId, newStatus);
      await loadUsers();
      await loadStats();
    } catch (err) {
      setError(err?.message || 'Failed to toggle user status');
      console.error('Error toggling status:', err);
    }
  };

  const handleResetPassword = async (userId) => {
    // TODO: Implement password reset functionality
    alert('Password reset functionality coming soon');
  };

  const handleToggleVoiceActor = async (userId, isVoiceActor) => {
    try {
      await userService?.toggleVoiceActorStatus(userId, isVoiceActor);
      await loadUsers();
      await loadStats();
    } catch (err) {
      setError(err?.message || 'Failed to update voice actor status');
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation 
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={[]}
      />
      <main className="main-content">
        <BreadcrumbTrail />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <Icon name="AlertCircle" className="text-red-500 mr-3 mt-0.5" size={20} />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
                <button 
                  onClick={() => setError('')}
                  className="text-red-500 hover:text-red-700"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">User Management</h1>
              <p className="text-muted-foreground">Manage student and administrator accounts</p>
            </div>
            <Button
              variant="default"
              onClick={() => setIsCreateModalOpen(true)}
              iconName="UserPlus"
              iconPosition="left"
            >
              Create New User
            </Button>
          </div>

          <UserStatsCards stats={stats} />

          <UserFilters 
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />

          <BulkActionsBar
            selectedCount={selectedUsers?.length}
            onBulkAction={handleBulkAction}
            onClearSelection={() => setSelectedUsers([])}
          />

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">
                  All Users ({sortedUsers?.length})
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedUsers?.length > 0 && `${selectedUsers?.length} selected`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                >
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Upload"
                  iconPosition="left"
                >
                  Import
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <Icon name="Loader" size={48} className="text-primary animate-spin mb-4" />
                <p className="text-muted-foreground">Loading users...</p>
              </div>
            ) : (
              <>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedUsers?.length === sortedUsers?.length && sortedUsers?.length > 0}
                            onChange={(e) => handleSelectAll(e?.target?.checked)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
                            aria-label="Select all users"
                          />
                        </th>
                        <th className="px-4 py-3 text-left">
                          <button
                            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150"
                            onClick={() => handleSort('name')}
                          >
                            User
                            <Icon name={sortConfig?.key === 'name' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left">
                          <button
                            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150"
                            onClick={() => handleSort('role')}
                          >
                            Role
                            <Icon name={sortConfig?.key === 'role' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left">
                          <button
                            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150"
                            onClick={() => handleSort('status')}
                          >
                            Status
                            <Icon name={sortConfig?.key === 'status' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left">
                          <button
                            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150"
                            onClick={() => handleSort('lastLogin')}
                          >
                            Last Login
                            <Icon name={sortConfig?.key === 'lastLogin' ? (sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors duration-150"
                          onClick={() => handleSort('registeredDate')}
                        >
                          <div className="flex items-center gap-2">
                            Registered
                            {sortConfig?.key === 'registeredDate' && (
                              <Icon name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Voice Actor
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers?.map(user => (
                        <UserTableRow
                          key={user?.id}
                          user={user}
                          onEdit={handleEditClick}
                          onDelete={handleDeleteUser}
                          onToggleStatus={handleToggleStatus}
                          onResetPassword={handleResetPassword}
                          onToggleVoiceActor={handleToggleVoiceActor}
                          isSelected={selectedUsers?.includes(user?.id)}
                          onSelect={handleSelectUser}
                        />
                      ))}
                    </tbody>
                  </table>

                  {sortedUsers?.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <Icon name="Users" size={64} className="text-muted-foreground mb-4" />
                      <p className="text-lg font-medium text-foreground mb-2">No users found</p>
                      <p className="text-muted-foreground text-center">
                        Try adjusting your filters or create a new user
                      </p>
                    </div>
                  )}
                </div>

                <div className="lg:hidden p-4">
                  {sortedUsers?.map(user => (
                    <MobileUserCard
                      key={user?.id}
                      user={user}
                      onEdit={handleEditClick}
                      onDelete={handleDeleteUser}
                      onToggleStatus={handleToggleStatus}
                      onResetPassword={handleResetPassword}
                      onToggleVoiceActor={handleToggleVoiceActor}
                    />
                  ))}

                  {sortedUsers?.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <Icon name="Users" size={64} className="text-muted-foreground mb-4" />
                      <p className="text-lg font-medium text-foreground mb-2">No users found</p>
                      <p className="text-muted-foreground text-center">
                        Try adjusting your filters or create a new user
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
      />
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleEditUser}
        user={selectedUser}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmDelete}
        userName={selectedUser?.name}
      />
    </div>
  );
};

export default UserManagement;